<template>
    <div class="perf-report">
        <!-- Header -->
        <div class="perf-report__header">
            <div>
                <h1 class="perf-report__title">Employee Performance Report</h1>
                <p class="perf-report__subtitle">Composite scoring based on attendance, punctuality, tasks, leave management &amp; effort</p>
            </div>
            <div class="perf-report__header-actions">
                <button class="perf-report__btn perf-report__btn--outline" @click="exportCSV" :disabled="!employees.length">
                    <i class="fas fa-download"></i> Export CSV
                </button>
            </div>
        </div>

        <!-- Filters -->
        <div class="perf-report__filters">
            <div class="perf-report__filter">
                <label class="perf-report__filter-label">Year</label>
                <select v-model="year" class="perf-report__select">
                    <option v-for="y in years" :key="y" :value="y">{{ y }}</option>
                </select>
            </div>
            <div class="perf-report__filter">
                <label class="perf-report__filter-label">Month</label>
                <select v-model="month" class="perf-report__select">
                    <option v-for="(m, i) in months" :key="i" :value="i + 1">{{ m }}</option>
                </select>
            </div>
            <div class="perf-report__filter">
                <label class="perf-report__filter-label">Company</label>
                <CompanyDropdown :companyId="companyId" :showTitle="false" @change="companyId = $event" />
            </div>
            <div class="perf-report__filter perf-report__filter--search">
                <label class="perf-report__filter-label">Search Employee</label>
                <div class="perf-report__search-wrap">
                    <i class="fas fa-search perf-report__search-icon"></i>
                    <input v-model="search" class="perf-report__search" placeholder="Search by name…" />
                </div>
            </div>
            <div class="perf-report__filter perf-report__filter--btn">
                <button class="perf-report__btn perf-report__btn--primary" @click="loadReport" :disabled="loading">
                    <i :class="loading ? 'fas fa-spinner fa-spin' : 'fas fa-chart-bar'"></i>
                    {{ loading ? "Loading…" : "Generate Report" }}
                </button>
            </div>
        </div>

        <!-- Summary Cards -->
        <div v-if="summary" class="perf-report__summary">
            <div class="perf-report__stat-card">
                <div class="perf-report__stat-icon" style="background: rgba(0,94,137,0.1); color: #005e89">
                    <i class="fas fa-users"></i>
                </div>
                <div>
                    <div class="perf-report__stat-value">{{ summary.totalEmployees }}</div>
                    <div class="perf-report__stat-label">Total Employees</div>
                </div>
            </div>
            <div class="perf-report__stat-card">
                <div class="perf-report__stat-icon" style="background: rgba(59,130,246,0.1); color: #3b82f6">
                    <i class="fas fa-chart-line"></i>
                </div>
                <div>
                    <div class="perf-report__stat-value">{{ summary.avgScore }}<small>%</small></div>
                    <div class="perf-report__stat-label">Average Score</div>
                </div>
            </div>
            <div class="perf-report__stat-card">
                <div class="perf-report__stat-icon" style="background: rgba(16,185,129,0.1); color: #10b981">
                    <i class="fas fa-trophy"></i>
                </div>
                <div>
                    <div class="perf-report__stat-value">{{ summary.topPerformers }}</div>
                    <div class="perf-report__stat-label">Top Performers (80+)</div>
                </div>
            </div>
            <div class="perf-report__stat-card">
                <div class="perf-report__stat-icon" style="background: rgba(239,68,68,0.1); color: #ef4444">
                    <i class="fas fa-exclamation-triangle"></i>
                </div>
                <div>
                    <div class="perf-report__stat-value">{{ summary.needsImprovement }}</div>
                    <div class="perf-report__stat-label">Needs Improvement (&lt;60)</div>
                </div>
            </div>
        </div>

        <!-- Charts Row -->
        <div v-if="employees.length" class="perf-report__charts">
            <!-- Score Distribution Chart -->
            <div class="perf-report__card perf-report__card--chart">
                <h3 class="perf-report__card-title">Score Distribution</h3>
                <apexchart type="donut" height="280" :options="donutOptions" :series="donutSeries" />
            </div>
            <!-- Top 10 Bar Chart -->
            <div class="perf-report__card perf-report__card--chart">
                <h3 class="perf-report__card-title">Top 10 Performers</h3>
                <apexchart type="bar" height="280" :options="barOptions" :series="barSeries" />
            </div>
        </div>

        <!-- Individual Drill-down (when employee selected) -->
        <div v-if="selectedEmployee" class="perf-report__card perf-report__drilldown">
            <div class="perf-report__drilldown-header">
                <div>
                    <h3 class="perf-report__card-title" style="margin-bottom: 2px">
                        {{ selectedEmployee.name }}
                        <span class="perf-report__grade-badge" :style="{ background: getGrade(selectedEmployee.scores.composite).bg, color: getGrade(selectedEmployee.scores.composite).color }">
                            {{ getGrade(selectedEmployee.scores.composite).label }}
                        </span>
                    </h3>
                    <p class="perf-report__drilldown-sub">{{ selectedEmployee.designation }} · {{ selectedEmployee.company }} · Rank #{{ selectedEmployee.rank }}</p>
                </div>
                <button class="perf-report__close-btn" @click="selectedEmployee = null">
                    <i class="fas fa-times"></i>
                </button>
            </div>
            <div class="perf-report__drilldown-body">
                <!-- Radar Chart -->
                <div class="perf-report__drilldown-chart">
                    <apexchart type="radar" height="300" :options="radarOptions" :series="radarSeries" />
                </div>
                <!-- Breakdown Table -->
                <div class="perf-report__drilldown-details">
                    <div class="perf-report__breakdown">
                        <div class="perf-report__breakdown-row" v-for="item in breakdownItems" :key="item.label">
                            <div class="perf-report__breakdown-label">
                                <i :class="item.icon" :style="{ color: item.color }"></i>
                                {{ item.label }}
                            </div>
                            <div class="perf-report__breakdown-bar-wrap">
                                <div class="perf-report__breakdown-bar" :style="{ width: item.score + '%', background: item.color }"></div>
                            </div>
                            <div class="perf-report__breakdown-score" :style="{ color: item.color }">{{ item.score }}%</div>
                        </div>
                    </div>
                    <div class="perf-report__detail-grid">
                        <div class="perf-report__detail-item">
                            <span class="perf-report__detail-label">Working Days</span>
                            <span class="perf-report__detail-value">{{ selectedEmployee.workingDays }}</span>
                        </div>
                        <div class="perf-report__detail-item">
                            <span class="perf-report__detail-label">Present</span>
                            <span class="perf-report__detail-value">{{ selectedEmployee.present }}</span>
                        </div>
                        <div class="perf-report__detail-item">
                            <span class="perf-report__detail-label">Absent</span>
                            <span class="perf-report__detail-value">{{ selectedEmployee.absent }}</span>
                        </div>
                        <div class="perf-report__detail-item">
                            <span class="perf-report__detail-label">Leaves</span>
                            <span class="perf-report__detail-value">{{ selectedEmployee.leaveDays }}</span>
                        </div>
                        <div class="perf-report__detail-item">
                            <span class="perf-report__detail-label">Late Arrivals</span>
                            <span class="perf-report__detail-value">{{ selectedEmployee.lateComing }}</span>
                        </div>
                        <div class="perf-report__detail-item">
                            <span class="perf-report__detail-label">OT Hours</span>
                            <span class="perf-report__detail-value">{{ selectedEmployee.otHours }}</span>
                        </div>
                        <div class="perf-report__detail-item">
                            <span class="perf-report__detail-label">Tasks Completed</span>
                            <span class="perf-report__detail-value">{{ selectedEmployee.taskStats.completed }} / {{ selectedEmployee.taskStats.total }}</span>
                        </div>
                        <div class="perf-report__detail-item">
                            <span class="perf-report__detail-label">Composite Score</span>
                            <span class="perf-report__detail-value perf-report__detail-value--score">{{ selectedEmployee.scores.composite }}%</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Employee Table -->
        <div v-if="employees.length" class="perf-report__card">
            <div class="perf-report__table-header">
                <h3 class="perf-report__card-title">Employee Rankings</h3>
                <span class="perf-report__table-count">{{ filteredEmployees.length }} employees</span>
            </div>
            <div class="perf-report__table-wrap">
                <table class="perf-report__table">
                    <thead>
                        <tr>
                            <th @click="sortBy('rank')" class="perf-report__th--sortable">#</th>
                            <th @click="sortBy('name')" class="perf-report__th--sortable">Employee</th>
                            <th @click="sortBy('designation')">Designation</th>
                            <th @click="sortBy('present')" class="perf-report__th--sortable perf-report__th--center">Present</th>
                            <th @click="sortBy('lateComing')" class="perf-report__th--sortable perf-report__th--center">Late</th>
                            <th @click="sortBy('taskStats')" class="perf-report__th--center">Tasks</th>
                            <th @click="sortBy('leaveDays')" class="perf-report__th--sortable perf-report__th--center">Leaves</th>
                            <th @click="sortBy('otHours')" class="perf-report__th--sortable perf-report__th--center">OT Hrs</th>
                            <th @click="sortBy('scores')" class="perf-report__th--sortable perf-report__th--center">Score</th>
                            <th class="perf-report__th--center">Grade</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="emp in filteredEmployees" :key="emp.srNo" class="perf-report__row" :class="{ 'perf-report__row--selected': selectedEmployee && selectedEmployee.name === emp.name }">
                            <td class="perf-report__rank">
                                <span v-if="emp.rank <= 3" class="perf-report__medal">{{ emp.rank === 1 ? '🥇' : emp.rank === 2 ? '🥈' : '🥉' }}</span>
                                <span v-else>{{ emp.rank }}</span>
                            </td>
                            <td>
                                <div class="perf-report__emp-name">{{ emp.name }}</div>
                                <div class="perf-report__emp-company">{{ emp.company }}</div>
                            </td>
                            <td class="perf-report__emp-desig">{{ emp.designation }}</td>
                            <td class="perf-report__td--center">{{ emp.present }} / {{ emp.workingDays }}</td>
                            <td class="perf-report__td--center">{{ emp.lateComing }}</td>
                            <td class="perf-report__td--center">{{ emp.taskStats.completed }}/{{ emp.taskStats.total }}</td>
                            <td class="perf-report__td--center">{{ emp.leaveDays }}</td>
                            <td class="perf-report__td--center">{{ emp.otHours }}</td>
                            <td class="perf-report__td--center">
                                <div class="perf-report__score-cell">
                                    <div class="perf-report__score-bar-bg">
                                        <div class="perf-report__score-bar" :style="{ width: emp.scores.composite + '%', background: getGrade(emp.scores.composite).color }"></div>
                                    </div>
                                    <span class="perf-report__score-num">{{ emp.scores.composite }}</span>
                                </div>
                            </td>
                            <td class="perf-report__td--center">
                                <span class="perf-report__grade-badge" :style="{ background: getGrade(emp.scores.composite).bg, color: getGrade(emp.scores.composite).color }">
                                    {{ getGrade(emp.scores.composite).label }}
                                </span>
                            </td>
                            <td>
                                <button class="perf-report__view-btn" @click="selectedEmployee = emp" title="View Details">
                                    <i class="fas fa-chart-radar"></i>
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- Empty State -->
        <div v-if="!loading && !employees.length && hasSearched" class="perf-report__empty">
            <i class="fas fa-chart-bar"></i>
            <p>No performance data found. Try selecting a different month or company.</p>
        </div>

        <!-- Initial State -->
        <div v-if="!loading && !employees.length && !hasSearched" class="perf-report__empty">
            <i class="fas fa-chart-line"></i>
            <p>Select filters above and click <strong>Generate Report</strong> to view performance data.</p>
        </div>
    </div>
</template>

<script>
import CompanyDropdown from "../../components/dropdowns/CompanyDropdown.vue"
import { buildPerformanceReport, getSummaryStats, getGrade } from "../../utils/performance"

export default {
    name: "EmployeePerformanceReport",
    components: { CompanyDropdown },
    data() {
        return {
            year: new Date().getFullYear(),
            month: new Date().getMonth() + 1,
            companyId: "",
            search: "",
            loading: false,
            hasSearched: false,
            employees: [],
            summary: null,
            selectedEmployee: null,
            sortField: "rank",
            sortAsc: true,
            years: [],
            months: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
        }
    },
    computed: {
        filteredEmployees() {
            let list = [...this.employees]
            if (this.search.trim()) {
                const q = this.search.toLowerCase()
                list = list.filter(e => e.name.toLowerCase().includes(q) || e.designation.toLowerCase().includes(q) || e.company.toLowerCase().includes(q))
            }
            return list
        },
        // Chart data
        donutSeries() {
            if (!this.summary) return []
            return [this.summary.topPerformers, this.summary.goodPerformers, this.summary.needsImprovement]
        },
        donutOptions() {
            return {
                chart: { type: "donut" },
                labels: ["Top (80+)", "Good (60-79)", "Needs Improvement (<60)"],
                colors: ["#10b981", "#3b82f6", "#ef4444"],
                legend: { position: "bottom", fontSize: "12px" },
                plotOptions: {
                    pie: {
                        donut: {
                            size: "60%",
                            labels: {
                                show: true,
                                total: { show: true, label: "Total", fontSize: "14px", fontWeight: 600, color: "#334155" },
                            },
                        },
                    },
                },
                dataLabels: { enabled: true, formatter: (v) => Math.round(v) + "%" },
            }
        },
        barSeries() {
            const top10 = this.employees.slice(0, 10)
            return [{ name: "Score", data: top10.map(e => e.scores.composite) }]
        },
        barOptions() {
            const top10 = this.employees.slice(0, 10)
            return {
                chart: { type: "bar", toolbar: { show: false } },
                plotOptions: {
                    bar: { borderRadius: 6, horizontal: true, barHeight: "60%", distributed: true },
                },
                colors: top10.map(e => getGrade(e.scores.composite).color),
                xaxis: {
                    categories: top10.map(e => e.name.split(" ").slice(0, 2).join(" ")),
                    max: 100,
                    labels: { formatter: (v) => Math.round(v) + "%" },
                },
                yaxis: { labels: { style: { fontSize: "11px" } } },
                dataLabels: { enabled: true, formatter: (v) => v + "%", style: { fontSize: "11px" } },
                legend: { show: false },
                tooltip: { enabled: true, y: { formatter: (v) => v + "%" } },
            }
        },
        // Radar chart for selected employee
        radarSeries() {
            if (!this.selectedEmployee) return []
            const s = this.selectedEmployee.scores
            return [{ name: this.selectedEmployee.name, data: [s.attendance, s.punctuality, s.tasks, s.leaveManagement, s.effort] }]
        },
        radarOptions() {
            return {
                chart: { type: "radar", toolbar: { show: false } },
                xaxis: { categories: ["Attendance", "Punctuality", "Tasks", "Leave Mgmt", "Effort"] },
                yaxis: { show: false, max: 100 },
                colors: ["#005e89"],
                fill: { opacity: 0.25 },
                stroke: { width: 2 },
                markers: { size: 4 },
                dataLabels: { enabled: true, formatter: (v) => v + "%", style: { fontSize: "10px" } },
            }
        },
        breakdownItems() {
            if (!this.selectedEmployee) return []
            const s = this.selectedEmployee.scores
            return [
                { label: "Attendance", score: s.attendance, icon: "fas fa-calendar-check", color: "#005e89" },
                { label: "Punctuality", score: s.punctuality, icon: "fas fa-clock", color: "#10b981" },
                { label: "Task Completion", score: s.tasks, icon: "fas fa-tasks", color: "#3b82f6" },
                { label: "Leave Management", score: s.leaveManagement, icon: "fas fa-umbrella-beach", color: "#f59e0b" },
                { label: "Extra Effort", score: s.effort, icon: "fas fa-fire", color: "#ef4444" },
            ]
        },
    },
    mounted() {
        const cur = new Date().getFullYear()
        this.years = [cur, cur - 1, cur - 2, cur - 3]
    },
    methods: {
        getGrade,
        async loadReport() {
            this.loading = true
            this.hasSearched = true
            this.selectedEmployee = null
            try {
                this.employees = await buildPerformanceReport(this.year, this.month, this.companyId)
                this.summary = getSummaryStats(this.employees)
            } catch (err) {
                console.error("Performance report error:", err)
                this.$toast.error("Failed to load performance data")
                this.employees = []
                this.summary = null
            }
            this.loading = false
        },
        sortBy(field) {
            if (this.sortField === field) {
                this.sortAsc = !this.sortAsc
            } else {
                this.sortField = field
                this.sortAsc = true
            }
            const dir = this.sortAsc ? 1 : -1
            this.employees.sort((a, b) => {
                let va, vb
                if (field === "scores") { va = a.scores.composite; vb = b.scores.composite }
                else if (field === "taskStats") { va = a.taskStats.completed; vb = b.taskStats.completed }
                else { va = a[field]; vb = b[field] }
                if (typeof va === "string") return va.localeCompare(vb) * dir
                return (va - vb) * dir
            })
            // Re-rank
            this.employees.forEach((e, i) => e.rank = i + 1)
        },
        exportCSV() {
            const headers = ["Rank", "Name", "Designation", "Company", "Working Days", "Present", "Absent", "Leaves", "Late", "OT Hours", "Tasks Done", "Tasks Total", "Attendance %", "Punctuality %", "Task %", "Leave Mgmt %", "Effort %", "Composite %", "Grade"]
            const rows = this.employees.map(e => [
                e.rank, e.name, e.designation, e.company, e.workingDays, e.present, e.absent, e.leaveDays, e.lateComing, e.otHours,
                e.taskStats.completed, e.taskStats.total,
                e.scores.attendance, e.scores.punctuality, e.scores.tasks, e.scores.leaveManagement, e.scores.effort, e.scores.composite,
                getGrade(e.scores.composite).label,
            ])
            const csv = [headers.join(","), ...rows.map(r => r.map(v => `"${v}"`).join(","))].join("\n")
            const blob = new Blob([csv], { type: "text/csv" })
            const url = URL.createObjectURL(blob)
            const a = document.createElement("a")
            a.href = url
            a.download = `performance-report-${this.year}-${this.month}.csv`
            a.click()
            URL.revokeObjectURL(url)
        },
    },
}
</script>

<style lang="scss" scoped>
$primary: #005e89;
$primaryLight: #e8f4fa;
$primaryDark: #003d5c;
$gray50: #f8fafc;
$gray100: #f1f5f9;
$gray200: #e2e8f0;
$gray300: #cbd5e1;
$gray400: #94a3b8;
$gray500: #64748b;
$gray600: #475569;
$gray700: #334155;
$gray800: #1e293b;
$white: #ffffff;
$success: #10b981;
$danger: #ef4444;
$warning: #f59e0b;
$info: #3b82f6;

.perf-report {
    padding: 0 8px;
    text-align: left;
}

// ─── Header ───
.perf-report__header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 20px;
    flex-wrap: wrap;
    gap: 12px;
}
.perf-report__title {
    font-size: 22px;
    font-weight: 700;
    color: $gray800;
    margin: 0 0 4px;
}
.perf-report__subtitle {
    font-size: 13px;
    color: $gray500;
    margin: 0;
}
.perf-report__header-actions {
    display: flex;
    gap: 8px;
}

// ─── Buttons ───
.perf-report__btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 9px 18px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    border: none;
    cursor: pointer;
    transition: all 0.2s;
    white-space: nowrap;
    &:disabled { opacity: 0.5; cursor: not-allowed; }
    &--primary {
        background: $primary;
        color: $white;
        &:hover:not(:disabled) { background: $primaryDark; }
    }
    &--outline {
        background: $white;
        color: $gray700;
        border: 1px solid $gray300;
        &:hover:not(:disabled) { border-color: $primary; color: $primary; }
    }
}

// ─── Filters ───
.perf-report__filters {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    align-items: flex-end;
    margin-bottom: 20px;
    padding: 16px;
    background: $white;
    border-radius: 12px;
    border: 1px solid $gray200;
}
.perf-report__filter {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 140px;
}
.perf-report__filter--search {
    flex: 1;
    min-width: 200px;
}
.perf-report__filter--btn {
    min-width: auto;
    justify-content: flex-end;
}
.perf-report__filter-label {
    font-size: 11px;
    font-weight: 600;
    color: $gray500;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}
.perf-report__select {
    padding: 8px 12px;
    border: 1px solid $gray200;
    border-radius: 8px;
    font-size: 13px;
    color: $gray700;
    background: $white;
    outline: none;
    &:focus { border-color: $primary; }
}
.perf-report__search-wrap {
    position: relative;
}
.perf-report__search-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: $gray400;
    font-size: 12px;
}
.perf-report__search {
    width: 100%;
    padding: 8px 12px 8px 32px;
    border: 1px solid $gray200;
    border-radius: 8px;
    font-size: 13px;
    color: $gray700;
    outline: none;
    &::placeholder { color: $gray400; }
    &:focus { border-color: $primary; }
}

// ─── Summary Cards ───
.perf-report__summary {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 12px;
    margin-bottom: 20px;
}
.perf-report__stat-card {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 16px;
    background: $white;
    border-radius: 12px;
    border: 1px solid $gray200;
}
.perf-report__stat-icon {
    width: 44px;
    height: 44px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    flex-shrink: 0;
}
.perf-report__stat-value {
    font-size: 22px;
    font-weight: 700;
    color: $gray800;
    line-height: 1.1;
    small { font-size: 14px; font-weight: 500; }
}
.perf-report__stat-label {
    font-size: 12px;
    color: $gray500;
    margin-top: 2px;
}

// ─── Cards ───
.perf-report__card {
    background: $white;
    border-radius: 12px;
    border: 1px solid $gray200;
    padding: 20px;
    margin-bottom: 20px;
}
.perf-report__card--chart {
    flex: 1;
    min-width: 300px;
}
.perf-report__card-title {
    font-size: 15px;
    font-weight: 700;
    color: $gray800;
    margin: 0 0 12px;
}
.perf-report__charts {
    display: flex;
    gap: 16px;
    margin-bottom: 0;
    flex-wrap: wrap;
}

// ─── Drill-down ───
.perf-report__drilldown {
    border-left: 4px solid $primary;
}
.perf-report__drilldown-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
}
.perf-report__drilldown-sub {
    color: $gray500;
    font-size: 13px;
    margin: 0;
}
.perf-report__close-btn {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: 1px solid $gray200;
    background: $white;
    color: $gray500;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    &:hover { background: $gray50; color: $gray700; }
}
.perf-report__drilldown-body {
    display: grid;
    grid-template-columns: 300px 1fr;
    gap: 20px;
    margin-top: 16px;
}
.perf-report__drilldown-chart {
    display: flex;
    align-items: center;
    justify-content: center;
}
.perf-report__breakdown {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-bottom: 16px;
}
.perf-report__breakdown-row {
    display: flex;
    align-items: center;
    gap: 10px;
}
.perf-report__breakdown-label {
    width: 140px;
    font-size: 12px;
    font-weight: 500;
    color: $gray600;
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
    i { width: 14px; text-align: center; }
}
.perf-report__breakdown-bar-wrap {
    flex: 1;
    height: 8px;
    background: $gray100;
    border-radius: 4px;
    overflow: hidden;
}
.perf-report__breakdown-bar {
    height: 100%;
    border-radius: 4px;
    transition: width 0.5s ease;
}
.perf-report__breakdown-score {
    width: 40px;
    font-size: 12px;
    font-weight: 700;
    text-align: right;
}
.perf-report__detail-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 8px;
}
.perf-report__detail-item {
    padding: 8px 12px;
    background: $gray50;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    gap: 2px;
}
.perf-report__detail-label {
    font-size: 11px;
    color: $gray500;
    font-weight: 500;
}
.perf-report__detail-value {
    font-size: 15px;
    font-weight: 700;
    color: $gray800;
    &--score { color: $primary; }
}
.perf-report__grade-badge {
    display: inline-block;
    padding: 2px 10px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    margin-left: 6px;
    vertical-align: middle;
}

// ─── Table ───
.perf-report__table-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
}
.perf-report__table-count {
    font-size: 12px;
    color: $gray500;
}
.perf-report__table-wrap {
    overflow-x: auto;
}
.perf-report__table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
    th {
        text-align: left;
        padding: 10px 12px;
        font-size: 11px;
        font-weight: 600;
        color: $gray500;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        border-bottom: 2px solid $gray200;
        white-space: nowrap;
    }
    td {
        padding: 10px 12px;
        border-bottom: 1px solid $gray100;
        vertical-align: middle;
    }
}
.perf-report__th--sortable {
    cursor: pointer;
    user-select: none;
    &:hover { color: $primary; }
}
.perf-report__th--center,
.perf-report__td--center {
    text-align: center;
}
.perf-report__row {
    transition: background 0.15s;
    &:hover { background: $gray50; }
    &--selected { background: $primaryLight !important; }
}
.perf-report__rank {
    font-weight: 700;
    color: $gray500;
    text-align: center;
    width: 40px;
}
.perf-report__medal {
    font-size: 16px;
}
.perf-report__emp-name {
    font-weight: 600;
    color: $gray800;
}
.perf-report__emp-company {
    font-size: 11px;
    color: $gray400;
}
.perf-report__emp-desig {
    color: $gray600;
}
.perf-report__score-cell {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 100px;
}
.perf-report__score-bar-bg {
    flex: 1;
    height: 6px;
    background: $gray100;
    border-radius: 3px;
    overflow: hidden;
}
.perf-report__score-bar {
    height: 100%;
    border-radius: 3px;
    transition: width 0.5s ease;
}
.perf-report__score-num {
    font-weight: 700;
    font-size: 12px;
    color: $gray700;
    min-width: 28px;
    text-align: right;
}
.perf-report__view-btn {
    width: 30px;
    height: 30px;
    border-radius: 8px;
    border: 1px solid $gray200;
    background: $white;
    color: $gray500;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    transition: all 0.15s;
    &:hover { border-color: $primary; color: $primary; background: $primaryLight; }
}

// ─── Empty ───
.perf-report__empty {
    text-align: center;
    padding: 60px 20px;
    background: $white;
    border-radius: 12px;
    border: 1px solid $gray200;
    i {
        font-size: 40px;
        color: $gray300;
        margin-bottom: 12px;
    }
    p {
        color: $gray500;
        font-size: 14px;
        margin: 0;
    }
}

// ─── Mobile ───
@media (max-width: 768px) {
    .perf-report__drilldown-body {
        grid-template-columns: 1fr;
    }
    .perf-report__filters {
        flex-direction: column;
    }
    .perf-report__filter {
        width: 100%;
    }
    .perf-report__charts {
        flex-direction: column;
    }
    .perf-report__summary {
        grid-template-columns: repeat(2, 1fr);
    }
}
</style>
